import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useCycle } from 'motion/react';
import { Link, useLocation } from 'react-router-dom';
import { WebsiteConfig } from '@wbk/contentful/api';
import { ChevronIcon } from '@wbk/svg';
import { Button, Typography } from '@wbk/ui';
import LanguageSwitch from '@/components/common/LanguageSwitch';
import useLocalization from '@/context/localization/useLocalization';
import useIsCurrentPath from '@/hooks/useIsCurrentPath';
import { useOnClickOutside } from '@/hooks/use-on-click-outside';
import { router } from '@/routes';
import GlobalSearch, { SearchButton } from '@/components/common/GlobalSearch';
import Menu from './Menu';
import Timer from './Timer';
import HeroCover from './HeaderCover';
import ContactModal, { ContactButton } from './ContactModal';

type Props = {
  config: WebsiteConfig;
};

const Header = ({ config }: Props) => {
  const [isOpen, toggleOpen] = useCycle(false, true);
  const [isGlobalSearchOpen, toggleOpenGlobalSearch] = useCycle(false, true);
  const [isContactModalOpen, toggleContactModal] = useCycle(false, true);
  const location = useLocation();
  const { locale } = useLocalization();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getTicket = config?.header?.topSubNavigationCollection?.items[0];
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useOnClickOutside(overlayRef as React.RefObject<HTMLElement>, () => {
    toggleOpen(0);
  });

  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = (e: React.MouseEvent) => {
    const related = e.relatedTarget as Node;
    if (
      overlayRef.current &&
      buttonRef.current &&
      !overlayRef.current?.contains(related) &&
      !buttonRef.current?.contains(related)
    ) {
      setIsHovered(false);
    }
  };

  const ticketPath = `/${locale}/hamza-cairokee-marwan-concert-rs25-tickets`;
  const laylatPath = `/${locale}/laylat-alomr`;
  const shouldHideTicket = useIsCurrentPath(ticketPath);
  const shouldHideLaylat = useIsCurrentPath(laylatPath);

  const shouldHideTicketOrLaylat = shouldHideTicket || shouldHideLaylat;

  return (
    <div className='fixed inset-x-0 top-0 z-40'>
      <HeroCover />
      <div className='container-padding container'>
        <header className='container z-50 mx-auto w-full px-3 xl:px-0'>
          <nav className='relative hidden w-full lg:block'>
            <div className='pointer-events-none absolute inset-x-0 z-50 flex items-center justify-center gap-4'>
              <div className='3xl:mt-[4vw] 3xl:max-w-[450px] 3xl:-translate-y-2 pointer-events-auto mt-[4vw] flex w-full items-center justify-between md:max-w-[220px] md:-translate-y-1 lg:max-w-[280px] xl:mt-16 xl:max-w-[350px] xl:-translate-y-2 xl:gap-10 2xl:mt-[4vw] 2xl:-translate-y-1'>
                {config.header.topNavigationCollection.items.map((link) => {
                  if (link.id === 'zones') {
                    return (
                      <button
                        key={link.id}
                        type='button'
                        className={twMerge(
                          'md:text-body-XS lg:!text-body-S xl:!text-body-M flex items-center gap-3 font-semibold uppercase text-white disabled:opacity-40',

                          location.pathname.includes(link.href) ? 'text-[#987BFF]' : ''
                        )}
                        ref={buttonRef}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => {
                          // go to zones page
                          router.navigate(`/${locale}/zones`);
                          setIsHovered(false);
                        }}
                      >
                        {link.label}
                        <ChevronIcon
                          className={twMerge(
                            'rotate-90 transition-transform',
                            isOpen || isHovered ? '!-rotate-90' : ''
                          )}
                        />
                      </button>
                    );
                  }
                  return (
                    <Typography key={link.id}>
                      <Link
                        to={`/${locale}${link.href}`}
                        className={twMerge(
                          'md:!text-body-XS lg:!text-body-S xl:!text-body-M font-semibold uppercase text-white',

                          location.pathname.includes(link.href) ? 'text-[#987BFF]' : ''
                        )}
                      >
                        {link?.label}
                      </Link>
                    </Typography>
                  );
                })}
              </div>
            </div>

            <div
              className={twMerge(
                'flex w-full items-center justify-between pt-14 2xl:pt-14',
                '3xl:pt-[3vw]'
              )}
            >
              <div className='pointer-events-auto flex items-center gap-6 2xl:gap-10'>
                <Link to={`/${locale}`} className='relative h-12 w-20 xl:w-28'>
                  {scrolled ? (
                    <div className='vibrant-glass-logo' aria-label='Logo' />
                  ) : (
                    <img
                      src='/images/logo.svg'
                      alt='Riyadh Season'
                      className='h-full w-full object-contain'
                    />
                  )}
                </Link>

                <Timer config={config} />
              </div>
              <div className='flex flex-wrap items-center gap-3 md:max-w-[265px] xl:mt-4 2xl:max-w-full'>
                <SearchButton
                  className={twMerge(scrolled && 'bg-[#000000ab] !backdrop-blur')}
                  onClick={() => toggleOpenGlobalSearch()}
                />
                <ContactButton
                  className={twMerge(scrolled && 'bg-[#000000ab] !backdrop-blur')}
                  onClick={() => toggleContactModal()}
                />
                <LanguageSwitch
                  className={twMerge(
                    scrolled && 'bg-[#000000ab] !backdrop-blur',
                    'text-primary border-primary rounded-full border px-4'
                  )}
                />
                {!shouldHideTicketOrLaylat && (
                  <Button className='!text-body-XS xl:!text-body-M rounded-full py-1 md:flex-grow 2xl:flex-grow-0 rtl:flex-row-reverse'>
                    <Link to={getTicket?.href || '/'} target='_blank' rel='noreferrer noopener'>
                      {getTicket?.label}
                    </Link>
                    <img src='/icons/ShareIcon.svg' alt='' className='mt-[1px]' />
                  </Button>
                )}
              </div>
            </div>
          </nav>
        </header>
      </div>

      {/* Menu Overlay */}
      <Menu
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        ref={overlayRef}
        config={config}
        isHovered={isHovered}
        onMouseLeave={handleMouseLeave}
        handleMouseEnter={handleMouseEnter}
        toggleOpenGlobalSearch={toggleOpenGlobalSearch}
        isGlobalSearchOpen={isGlobalSearchOpen}
        scrolled={scrolled}
      />

      <GlobalSearch open={isGlobalSearchOpen} onOpenChange={toggleOpenGlobalSearch} />
      <ContactModal open={isContactModalOpen} onOpenChange={toggleContactModal} />
    </div>
  );
};

export default Header;
